import { applyMiddleware, createStore } from 'redux';
import thunk, { ThunkMiddleware } from 'redux-thunk';
import rootReducer from './reducers';
import { composeWithDevTools } from '@redux-devtools/extension';
import {
  buildArmyListState,
  buildArmyListUnitState,
  buildClubsState,
  buildDraftState,
  buildDraftTeamState,
  buildDrawState,
  buildFolderState,
  buildMyBuilderState,
  buildMyContestState,
  MyBuilderActionState,
} from '@Stores/index';
import * as Sentry from '@sentry/react';

import { DraftMapper } from '@Mapper/tool/pairing/DraftMapper';
import { TYPES } from '@Types';
import { container } from '@Container';
import { PayloadAction } from '@reduxjs/toolkit';
import { FormAction } from 'redux-form/lib/actions';

export type AppState = ReturnType<typeof rootReducer>;

export type Action = PayloadAction<any> | FormAction;

const composeEnhancers = composeWithDevTools({ trace: true, traceLimit: 25 });

export default function configureStore(initialState: any) {
  let data = {
    myBuilder: buildMyBuilderState({
      entity: null,
    }),
    armyList: buildArmyListState({
      entity: null,
      id: null,
    }),
    folder: buildFolderState({
      entity: null,
      id: null,
    }),
    myContest: buildMyContestState({
      entity: null,
      id: null,
    }),

    // Federation
    clubs: buildClubsState({
      entity: null,
    }),

    // Tool - Pairing
    draft: buildDraftState({
      entity: null,
      id: null,
    }),
    myTeam: buildDraftTeamState({
      entity: null,
    }),
    opponentTeam: buildDraftTeamState({
      entity: null,
    }),
    myDraw: buildDrawState({
      entity: null,
    }),
    opponentDraw: buildDrawState({
      entity: null,
    }),
  } as AppState;

  if (initialState.army_list_id) {
    data.armyList.id = initialState.army_list_id;
  }
  if (initialState.folder_id) {
    data.folder.id = initialState.folder_id;
  }
  if (initialState.contest_id) {
    data.myContest.id = initialState.contest_id;
  }
  if (initialState.army_list_unit_id) {
    data.armyListUnit = buildArmyListUnitState({
      id: initialState.army_list_unit_id,
      entity: null,
      name: null,
    });
  }
  if (initialState.action) {
    switch (initialState.action) {
      case 'REQUEST_CREATION_ARMY_LIST':
        data.myBuilder.entity = MyBuilderActionState.ArmyListCreation;
        break;
      case 'REQUEST_CREATION_ARSENAL_ARMY':
        data.myBuilder.entity = MyBuilderActionState.ArsenalArmyCreation;
        break;
      case 'REQUEST_FORMAT_ARMY_LIST':
        data.myBuilder.entity = MyBuilderActionState.ArmyListFormat;
        break;
      case 'REQUEST_CREATION_CONTEST':
        data.myBuilder.entity = MyBuilderActionState.ContestCreation;
        break;
      case 'REQUEST_CREATION_FOLDER':
        data.myBuilder.entity = MyBuilderActionState.FolderCreation;
        break;
    }
  }

  // Tool - Pairing
  if (initialState.draft) {
    const draftMapper = container.get<DraftMapper>(TYPES.Mapper.DraftMapper);
    const draft = draftMapper.map(initialState.draft);

    data.draft = buildDraftState({
      entity: draftMapper.map(initialState.draft),
      id: initialState.draft.id,
    });
    data.myTeam = buildDraftTeamState({
      entity: draft.myTeam,
    });
    data.opponentTeam = buildDraftTeamState({
      entity: draft.opponentTeam,
    });
    data.myDraw = buildDrawState({
      entity: draft.myTeam?.draw ?? null,
    });
    data.opponentDraw = buildDrawState({
      entity: draft.opponentTeam?.draw ?? null,
    });
  }

  const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    // Optionally pass options listed below
  });

  const store = createStore<AppState, Action, {}, {}>(
    rootReducer,
    data,
    composeEnhancers(
      applyMiddleware(thunk as ThunkMiddleware<AppState, Action>),
      sentryReduxEnhancer,
    ),
  );

  return store;
}
